import clsx from "clsx";
import { isEmpty } from "lodash";
import {
  Card,
  ClassificationCounters,
  KeyValueList,
  RenderWhen,
  ThreatStatusLabel,
  Time
} from "../../../../../../components";
import { ThreatIntelligence } from "../../../../@types";
import { SECTIONS } from "../../../../consts";
import { CardSubtitle } from "../../components";

import { getClassificationByValue } from "../../../../../../lib/enums/ThreatStatusEnum";
import {
  useGetSampleAnalysisStatus,
  useLocalSampleSummaryParams,
  useReanalyze
} from "../../../../hooks";
import styles from "../analysis-card.module.scss";

type Props = {
  data?: ThreatIntelligence | null;
  decisive?: boolean;
  onClick: (section: string) => void;
};

// Pick newer value between time that was passed in cloud data and analysis status time
const getAnalysisTime = (dataTime?: string, statusTime?: string) => {
  if (!dataTime && !statusTime) return undefined;
  if (!dataTime) return statusTime;
  if (!statusTime) return dataTime;
  return new Date(dataTime).getTime() >= new Date(statusTime).getTime()
    ? dataTime
    : statusTime;
};

const ThreatIntelligenceAnalysis = ({ data, decisive, onClick }: Props) => {
  const { hash, sourceId, relationshipId } = useLocalSampleSummaryParams();
  const { mutate: reanalyze } = useReanalyze();

  const { data: { ti_cloud: cloud = undefined } = {} } =
    useGetSampleAnalysisStatus(hash);

  const handleOnClick = () => {
    onClick(SECTIONS.THREAT_INTELLIGENCE.MULTISCANNER);
  };

  const classificationValue = getClassificationByValue(
    data?.classification
  )?.toLowerCase();

  const analysisTime = getAnalysisTime(
    data?.analysis_time,
    cloud?.last_completed_on
  );

  const handleAnalyze = () => {
    if (!sourceId) return;
    reanalyze({
      samples: [
        {
          hash_value: hash,
          source_id: sourceId,
          relationship_id: relationshipId
        }
      ],
      integrations: ["ti_cloud"]
    });
  };

  return (
    <Card
      className={clsx(
        decisive && styles[`decisive_reason_${classificationValue}`]
      )}
      disabled={!data}
      icon="cloud-intelligence"
      title="Threat Intelligence"
      subtitle={
        <CardSubtitle
          impactsFinalClassification
          additionalInfo={cloud?.additional_information}
          displayStatus={cloud?.display_status}
          hasData={!isEmpty(data)}
          type="TI_CLOUD"
          onAnalyze={handleAnalyze}
        >
          <RenderWhen condition={Boolean(data)}>
            <div className="flex align-center flex-gap-1">
              <ThreatStatusLabel
                className={styles.analysis_card_subtitle_classification}
                threatStatus={data?.classification}
                showOrb={false}
                showColor
              />
              <RenderWhen condition={Boolean(decisive)}>
                <span
                  className={clsx(
                    styles.decisive_reason_label,
                    styles[classificationValue]
                  )}
                >
                  (Decisive)
                </span>
              </RenderWhen>
            </div>
          </RenderWhen>
        </CardSubtitle>
      }
      data-testid="report_summary_threat_intelligence"
    >
      <KeyValueList
        className={styles.analysis_card_content_wrapper}
        onClick={handleOnClick}
        data-testid="report_summary_threat_intelligence_list"
      >
        <KeyValueList.Item
          className={styles.list_item_threat_intelligence}
          keyClassName="font-medium"
          label="Analysis Time: "
          value={
            <Time
              className={clsx(styles.time, "overflow-ellipsis")}
              time={analysisTime}
            />
          }
          data-testid="report_summary_threat_intelligence_analysis_time"
        />
        <KeyValueList.Item
          className={styles.list_item_threat_intelligence}
          keyClassName="font-medium"
          label="No. of Scans: "
          value={
            <div className={styles.list_item_value}>
              {data?.scan_number ?? 0}
            </div>
          }
          data-testid="report_summary_threat_intelligence_num_of_scans"
        />
        <KeyValueList.Item
          className={styles.list_item_threat_intelligence}
          keyClassName="font-medium"
          label="Next-Gen Antivirus: "
          value={
            <div className={styles.list_item_value}>
              {data?.next_gen_matches_count}/{data?.next_gen_scanners_count}{" "}
              Matches
            </div>
          }
          data-testid="report_summary_threat_intelligence_next_gen_av"
        />
        <KeyValueList.Item
          className={styles.list_item_threat_intelligence}
          keyClassName="font-medium"
          label="Traditional Antivirus: "
          value={
            <div className={styles.list_item_value}>
              {data?.regular_matches_count}/{data?.regular_scanners_count}{" "}
              Matches
            </div>
          }
          data-testid="report_summary_threat_intelligence_traditional_av"
        />
        <KeyValueList.Item
          className={clsx(styles.list_item_threat_intelligence, styles.rha)}
          keyClassName="font-medium"
          label="RHA File Similarity: "
          value={
            <ClassificationCounters
              className={styles.classification_counters}
              counts={{
                malicious: data?.rha?.malicious ?? 0,
                suspicious: data?.rha?.suspicious ?? 0,
                goodware: data?.rha?.goodware ?? 0
              }}
            />
          }
          data-testid="report_summary_threat_intelligence_rha"
        />
      </KeyValueList>
    </Card>
  );
};

export default ThreatIntelligenceAnalysis;
